import { useRecoilState } from 'recoil';
import { emptyPlaceholderDialogState } from 'domains/reporter/Reporter/state';
import SuppressibleDialogConfirm from 'common/ui/DialogConfirm/SuppressibleDialogConfirm';
import { useFields } from 'domains/reporter/Reporter/Fields/Fields.Context';
import { moveSelectionToFirstEmptyPlaceholder } from 'domains/reporter/RichTextEditor/plugins/placeholder/utils';
import { EmptyPlaceholderDialogContent } from 'domains/reporter/RichTextEditor/plugins/placeholder/components/EmptyPlaceholderDialogContent';
import { DEFAULT_FIELDS_WARNING_MODAL_PROPS } from './constants';
import { useReportExitActionData } from 'domains/reporter/RichTextEditor/analytics/useReportExitActionData';
import { useSlateStatic } from 'domains/reporter/RichTextEditor/core';
import { telemetryContextFor } from 'modules/Apollo/types';

export const EmptyPlaceholderFieldsWarning = (): React.ReactElement => {
  const editor = useSlateStatic();
  const [{ content }, { handleSubmit: onSubmit }] = useFields();

  const [placeholderFieldDialogState, setPlaceholderFieldDialogState] = useRecoilState(
    emptyPlaceholderDialogState
  );

  const { measureReportExitActionWithDialog } = useReportExitActionData();

  const handleCloseEmptyMergeFieldsDialog = () => {
    setPlaceholderFieldDialogState({
      ...placeholderFieldDialogState,
      open: false,
    });

    if (editor != null) {
      moveSelectionToFirstEmptyPlaceholder(content, editor);
    }
  };

  const handleConfirm = async () => {
    setPlaceholderFieldDialogState({
      ...placeholderFieldDialogState,
      open: false,
    });

    await measureReportExitActionWithDialog();

    await onSubmit({ force: true });
  };

  return (
    <SuppressibleDialogConfirm
      {...DEFAULT_FIELDS_WARNING_MODAL_PROPS}
      contentWidth="100%"
      dialogSettingId="empty-merge-fields-dialog"
      settingsContext={APOLLO_QUERY_CONTEXT}
      open={placeholderFieldDialogState.open}
      slug="empty-merge-fields-dialog"
      onConfirm={handleConfirm}
      confirmText="Sign anyway"
      confirmButtonVariant="ghost"
      confirmButtonCss={BUTTON_COMPACT_PADDING}
      cancelText="Fix fields"
      cancelButtonVariant="secondary"
      cancelButtonCss={BUTTON_COMPACT_PADDING}
      onCancel={handleCloseEmptyMergeFieldsDialog}
      headerText="Missing Fields"
      headerVariant="warning"
      headerCloseAriaLabel="Close missing fields dialog"
    >
      <EmptyPlaceholderDialogContent leftAligned />
    </SuppressibleDialogConfirm>
  );
};

EmptyPlaceholderFieldsWarning.displayName = 'EmptyPlaceholderFieldsWarning';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(EmptyPlaceholderFieldsWarning);
const BUTTON_COMPACT_PADDING = 'padding: 0.6rem 1.8rem;';
